import { SettingOutlined } from "@ant-design/icons"
import { Button, Tabs } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../constants/routes.ts"
import { useCommunicationRights } from "./communication-permissions.ts"

export interface CommunicationSection {
  key: string
  labelKey: string
  isVisible: (rights: ReturnType<typeof useCommunicationRights>) => boolean
}

const readsMessages = (rights: ReturnType<typeof useCommunicationRights>) =>
  rights.send || rights.viewAll

export const COMMUNICATION_SECTIONS: CommunicationSection[] = [
  {
    key: RoutesPaths.communication.overview,
    labelKey: "communication.overview.nav",
    isVisible: readsMessages
  },
  {
    key: RoutesPaths.communication.messages,
    labelKey: "communication.messages.nav",
    isVisible: readsMessages
  },
  {
    key: RoutesPaths.communication.campaigns,
    labelKey: "communication.campaigns.nav",
    isVisible: (rights) => rights.bulkSend || rights.viewAll
  },
  {
    key: RoutesPaths.communication.automations,
    labelKey: "communication.automations.nav",
    isVisible: (rights) => rights.settings
  }
]

export const CommunicationHome = ({
  fallback
}: {
  fallback: React.ReactNode
}) => {
  const rights = useCommunicationRights()
  return readsMessages(rights) ? (
    <Navigate to={RoutesPaths.communication.overview} replace />
  ) : (
    <>{fallback}</>
  )
}

const CommunicationLayout = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const rights = useCommunicationRights()
  const sections = COMMUNICATION_SECTIONS.filter((section) =>
    section.isVisible(rights)
  )
  const active = sections.find((section) =>
    pathname.startsWith(section.key)
  )?.key

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-start text-lg font-semibold">
          {t("communication.title")}
        </h2>
        {(rights.settings || rights.templates) &&
          !pathname.startsWith(RoutesPaths.communication.settings) && (
            <Button
              icon={<SettingOutlined aria-hidden />}
              onClick={() => navigate(RoutesPaths.communication.settings)}
              aria-label={t("communication.settings.open")}
            >
              {t("communication.settings.title")}
            </Button>
          )}
      </div>
      {sections.length > 0 && (
        <Tabs
          activeKey={active ?? ""}
          onChange={(key) => navigate(key)}
          items={sections.map((section) => ({
            key: section.key,
            label: t(section.labelKey)
          }))}
        />
      )}
      <Outlet />
    </div>
  )
}

export default CommunicationLayout
